// External packages
import * as ReactAria from 'react-aria-components';
import * as ReactHookForm from 'react-hook-form';
import * as React from 'react';

// Components
import * as Forms from 'components/Forms';
import { Icon } from 'components/Icons';

// Utils
import { twMerge } from 'tailwind-merge';

/* Select */

export type SelectProps<T extends object> = Omit<
  ReactAria.SelectProps<T>,
  'children'
> & {
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  label?: string;
  labelProps?: Forms.LabelProps;
  wrapperClassName?: string;
  clearable?: boolean;
};

export const Select = React.forwardRef(function Select<T extends object>(
  {
    label,
    labelProps,
    className,
    wrapperClassName,
    children,
    items,
    clearable = false,
    ...props
  }: SelectProps<T>,
  ref: React.Ref<HTMLButtonElement>
) {
  const hasSelection =
    props.selectedKey && props.selectedKey !== '' && props.selectedKey !== null;
  const renderChildren = () => {
    return (
      <>
        {clearable && hasSelection && (
          <Option key="__clear__" id="__clear__" className="text-gray-400">
            Clear selection
          </Option>
        )}
        {typeof children === 'function' && items
          ? Array.from(items).map(children)
          : children}
      </>
    );
  };

  const handleSelectionChange = (value: any) => {
    if (value === '__clear__' && props.onSelectionChange) {
      props.onSelectionChange('');
    } else if (props.onSelectionChange) {
      props.onSelectionChange(value);
    }
  };

  return (
    <ReactAria.Select
      {...props}
      className={twMerge('group', wrapperClassName)}
      onSelectionChange={handleSelectionChange}
      selectedKey={props.selectedKey === null ? '' : props.selectedKey}
    >
      {label && <Forms.Label {...labelProps}>{label}</Forms.Label>}
      <SelectTriggerButton ref={ref} className={className as string}>
        <ReactAria.SelectValue />
        <Icon
          name="chevron-down"
          aria-hidden="true"
          className="pointer-events-none size-5"
        />
      </SelectTriggerButton>
      <Popover maxHeight={220} placement="bottom right">
        <ReactAria.ListBox items={items}>{renderChildren()}</ReactAria.ListBox>
      </Popover>
    </ReactAria.Select>
  );
});

/* SelectField */

export type SelectFieldProps = Omit<
  ReactAria.SelectProps<object>,
  'children'
> & {
  items?: Iterable<object>;
  children: React.ReactNode | ((item: object) => React.ReactNode);
  label?: string;
  labelProps?: Forms.LabelProps;
  subLabelProps?: Forms.SubLabelProps;
  wrapperClassName?: string;
  clearable?: boolean;
};

export const SelectField = ({
  name,
  label,
  labelProps,
  subLabelProps,
  isInvalid,
  onSelectionChange,
  onBlur,
  className,
  wrapperClassName,
  children,
  items,
  clearable = false,
  ...props
}: SelectFieldProps) => {
  const { field, fieldState } = ReactHookForm.useController<
    { __name__: string },
    '__name__'
  >({ name: name as '__name__' });

  const hasSelection =
    field.value && field.value !== '' && field.value !== null;
  const renderChildren = () => {
    return (
      <>
        {clearable && hasSelection && (
          <Option key="__clear__" id="__clear__" className="text-gray-400">
            Clear selection
          </Option>
        )}
        {typeof children === 'function' && items
          ? Array.from(items).map(children)
          : children}
      </>
    );
  };

  const handleSelectionChange = (value: any) => {
    if (value === '__clear__') {
      field.onChange('');
      if (onSelectionChange) onSelectionChange('');
    } else {
      field.onChange(value);
      if (onSelectionChange) onSelectionChange(value);
    }
  };

  return (
    <ReactAria.Select
      {...field}
      {...props}
      id={name}
      name={name}
      selectedKey={field.value ?? ''}
      isInvalid={Boolean(fieldState.error) ?? isInvalid}
      onSelectionChange={handleSelectionChange}
      onBlur={(event) => {
        field.onBlur();
        if (onBlur) {
          onBlur(event);
        }
      }}
      className={twMerge('group', wrapperClassName)}
    >
      <Forms.Label {...labelProps} htmlFor={name}>
        {label}
      </Forms.Label>
      <SelectTriggerButton className={className as string}>
        <ReactAria.SelectValue />
        <Icon
          name="chevron-down"
          aria-hidden="true"
          className="pointer-events-none size-5"
        />
      </SelectTriggerButton>
      <Popover maxHeight={220}>
        <ReactAria.ListBox items={items}>{renderChildren()}</ReactAria.ListBox>
      </Popover>
      {fieldState.error && (
        <Forms.SubLabel
          {...subLabelProps}
          role="alert"
          slot="errorMessage"
          colorScheme="error"
        >
          {fieldState.error.message}
        </Forms.SubLabel>
      )}
    </ReactAria.Select>
  );
};

export const Popover: React.FC<
  React.ComponentProps<typeof ReactAria.Popover>
> = ({ className, ...props }) => {
  return (
    <ReactAria.Popover
      {...props}
      className={twMerge(
        'peer data-entering:animate-in data-entering:fade-in data-exiting:animate-out data-exiting:fade-out min-w-[var(--trigger-width)] overflow-y-auto rounded-lg border border-gray-200 bg-white',
        className as string
      )}
    />
  );
};

export const Option: React.FC<ReactAria.ListBoxItemProps> = ({
  className,
  ...props
}) => {
  return (
    <ReactAria.ListBoxItem
      {...props}
      className={twMerge(
        'cursor-pointer px-4 py-2 transition-colors  not-data-disabled:hover:bg-gray-50 data-disabled:cursor-not-allowed data-disabled:text-gray-200 data-selected:font-bold',
        className as string
      )}
    />
  );
};

export const SelectTriggerButton = React.forwardRef<
  HTMLButtonElement,
  ReactAria.ButtonProps
>(({ className, ...props }, ref) => {
  return (
    <ReactAria.Button
      {...props}
      ref={ref}
      className={twMerge(
        'inline-flex min-h-10 w-full shrink-0 cursor-pointer appearance-none items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-4 transition-all outline-none group-data-invalid:border-red-600 group-data-invalid:text-red-600 group-data-invalid:placeholder:text-red-600 group-data-invalid:hover:border-red-800 valid:hover:border-gray-300 focus:border-gray-300 data-disabled:cursor-not-allowed data-disabled:border-gray-200 data-disabled:bg-gray-50 data-disabled:text-gray-200',
        className as string
      )}
    />
  );
});
